// LiveNow component: displays a badge and list of streamers

import React, { useState, useEffect } from 'react';
import { cn } from '@/utilities/cn';
import Icon from '@/components/common/Icon';
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
} from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSystemWindowSize } from '../../hooks/useSystemWindowSize';
import { analyticsService } from '../../services/analyticsService';
import { getLiveNowData } from '../../transport/api/publicApi';
import { LiveStreamer } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('LiveNow');

// Header shell uses z-[200]/z-[210]; keep LiveNow overlays above it (Sheet is z-[320]).
const HEADER_OVERLAY_Z = 'z-[220]';

// Density map — resolved via JS width, not CSS class stacking, to avoid
// Tailwind arbitrary-breakpoint ordering issues with named breakpoints:
//
//   <sm (<640)       → Compact tap-to-expand
//   sm (640–767)     → 2 faces + LIVE + Twitch
//   md (768–1023)    → 3 faces + LIVE + Twitch (hamburger frees up space)
//   1024–1089        → Compact tap-to-expand (tight: full nav + logo)
//   1090–1279        → 1 face + LIVE + Twitch
//   xl (1280–1535)   → 2 faces + LIVE + Twitch
//   2xl (≥1536)      → 3 faces + LIVE + Twitch
type Tier =
  | { mode: 'hidden' }
  | { mode: 'compact' }
  | { mode: 'regular'; faces: 1 | 2 | 3 };

const resolveTier = (width: number): Tier => {
  if (width < 375) return { mode: 'hidden' };
  if (width < 640) return { mode: 'compact' };
  if (width < 768) return { mode: 'regular', faces: 2 };
  if (width < 1024) return { mode: 'regular', faces: 3 };
  if (width < 1090) return { mode: 'compact' };
  if (width < 1280) return { mode: 'regular', faces: 1 };
  if (width < 1536) return { mode: 'regular', faces: 2 };
  return { mode: 'regular', faces: 3 };
};

const initialsFrom = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

type StreamerClickSource = 'avatar' | 'overflow-list' | 'compact-list';

const trackStreamerClick = (
  streamer: LiveStreamer,
  source: StreamerClickSource
) => {
  analyticsService.trackButtonClick('LiveNow:Streamer', {
    twitch_login: streamer.twitchLogin,
    display_name: streamer.displayName,
    source,
  });
};

type StreamerAvatarProps = {
  streamer: LiveStreamer;
};

const StreamerAvatar: React.FC<StreamerAvatarProps> = ({ streamer }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <a
        href={streamer.twitchUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Watch ${streamer.displayName} on Twitch`}
        onClick={() => trackStreamerClick(streamer, 'avatar')}
      >
        <Avatar className="transition-transform hover:scale-105">
          <AvatarImage
            src={streamer.profileImageUrl}
            alt={`${streamer.displayName} Twitch avatar`}
          />
          <AvatarFallback>{initialsFrom(streamer.displayName)}</AvatarFallback>
        </Avatar>
      </a>
    </TooltipTrigger>
    <TooltipContent className={HEADER_OVERLAY_Z}>
      {streamer.displayName}
    </TooltipContent>
  </Tooltip>
);

type StreamerListProps = {
  streamers: LiveStreamer[];
  heading?: string;
  source: StreamerClickSource;
};

const StreamerList: React.FC<StreamerListProps> = ({
  streamers,
  heading,
  source,
}) => (
  <>
    {heading && (
      <div className="mb-1 px-1 text-xs font-medium text-muted-foreground">
        {heading}
      </div>
    )}
    <ul className="flex flex-col gap-1">
      {streamers.map((streamer) => (
        <li key={streamer.twitchLogin}>
          <a
            href={streamer.twitchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-muted"
            onClick={() => trackStreamerClick(streamer, source)}
          >
            <Avatar size="sm">
              <AvatarImage
                src={streamer.profileImageUrl}
                alt={`${streamer.displayName} Twitch avatar`}
              />
              <AvatarFallback>
                {initialsFrom(streamer.displayName)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate text-sm">{streamer.displayName}</span>
          </a>
        </li>
      ))}
    </ul>
  </>
);

type OverflowCountProps = {
  streamers: LiveStreamer[];
};

const OverflowCount: React.FC<OverflowCountProps> = ({ streamers }) => (
  <HoverCard openDelay={100} closeDelay={100}>
    <HoverCardTrigger asChild>
      <button
        type="button"
        className="relative flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground ring-2 ring-background hover:bg-muted/80"
        aria-label={`${streamers.length} more streamers live`}
      >
        +{streamers.length}
      </button>
    </HoverCardTrigger>
    <HoverCardContent align="end" className={cn(HEADER_OVERLAY_Z, 'w-56 p-2')}>
      <StreamerList
        streamers={streamers}
        heading="Also live"
        source="overflow-list"
      />
    </HoverCardContent>
  </HoverCard>
);

const LiveBadge: React.FC = () => (
  <Badge
    className="h-6 gap-1 border-transparent bg-red-500 px-2 text-[11px] font-semibold tracking-wide text-white hover:bg-red-500 hover:text-white"
    aria-label="Live on Twitch"
  >
    <span
      className="size-1.5 rounded-full bg-white animate-pulse"
      aria-hidden="true"
    />
    LIVE
  </Badge>
);

const TwitchIcon: React.FC = () => (
  <Icon name="twitch" size={18} className="text-purple-400" disabled />
);

const PILL_CONTAINER =
  'inline-flex items-center gap-2 rounded-full bg-background/80 px-2 py-1.5 shadow-sm ring-1 ring-border backdrop-blur';

// Compact tap-to-expand variant — uses Popover so tap works on mobile.
const CompactLiveNow: React.FC<LiveNowViewProps> = ({
  streamers,
  className,
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className={cn(PILL_CONTAINER, 'cursor-pointer', className)}
        aria-label={`${streamers.length} streamers live — view list`}
      >
        <LiveBadge />
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className={cn(HEADER_OVERLAY_Z, 'w-60 p-2')}>
      <StreamerList
        streamers={streamers}
        heading="Live now on Twitch"
        source="compact-list"
      />
    </PopoverContent>
  </Popover>
);

// Regular variant with faces.
const RegularLiveNow: React.FC<LiveNowViewProps & { faces: 1 | 2 | 3 }> = ({
  streamers,
  className,
  faces,
}) => {
  const visible = streamers.slice(0, faces);
  const overflow = streamers.slice(faces);

  return (
    <div className={cn(PILL_CONTAINER, className)}>
      <LiveBadge />
      <TwitchIcon />
      <AvatarGroup
        className="-ml-1 flex"
        aria-label={`${streamers.length} streamers live`}
      >
        {visible.map((streamer) => (
          <StreamerAvatar key={streamer.twitchLogin} streamer={streamer} />
        ))}
        {overflow.length > 0 && <OverflowCount streamers={overflow} />}
      </AvatarGroup>
    </div>
  );
};

type LiveNowViewProps = {
  streamers: LiveStreamer[];
  className?: string;
};

export const LiveNowView: React.FC<LiveNowViewProps> = ({
  streamers,
  className,
}) => {
  const { width } = useSystemWindowSize();

  if (streamers.length === 0) return null;
  // Avoid an SSR/first-paint flash: wait for a real measurement.
  if (width === 0) return null;

  const tier = resolveTier(width);
  if (tier.mode === 'hidden') return null;

  return (
    <TooltipProvider>
      {tier.mode === 'compact' ? (
        <CompactLiveNow streamers={streamers} className={className} />
      ) : (
        <RegularLiveNow
          streamers={streamers}
          className={className}
          faces={tier.faces}
        />
      )}
    </TooltipProvider>
  );
};

const LiveNow: React.FC<{ className?: string }> = ({ className }) => {
  const [streamers, setStreamers] = useState<LiveStreamer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLiveStreamers = async () => {
      try {
        const response = await getLiveNowData();
        if (response.success && response.data) {
          setStreamers(response.data.liveStreamers);
        }
      } catch (err) {
        logger.error('Failed to fetch live streamers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveStreamers();
  }, []);

  if (loading || streamers.length === 0) return null;

  return <LiveNowView streamers={streamers} className={className} />;
};

export default LiveNow;
